import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Car, MapPin, Play, Square, ClipboardCheck, AlertTriangle, Navigation, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { WorkSessionCard } from "@/components/routing/WorkSessionCard";

export default function DriverWorkspace() {
  const [data, setData] = useState(null);
  const [inspectOpen, setInspectOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [issue, setIssue] = useState({ type: "MECHANICAL", severity: "LOW", description: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/driver/me"); setData(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const vehicleId = data?.vehicle?.id;

  const startTrip = async (id) => {
    setBusy(true);
    try { await api.post(`/trips/${id}/start`); toast.success("Trip started"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const endTrip = async (id) => {
    setBusy(true);
    try { await api.post(`/trips/${id}/end`); toast.success("Trip completed"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const shareLocation = (id) => {
    if (!navigator.geolocation) { toast.error("This device does not support location."); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => pushLocation(id, pos.coords.latitude, pos.coords.longitude, pos.coords.speed, pos.coords.heading),
      () => toast.error("Location unavailable — allow location access in your browser."),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  const pushLocation = async (id, lat, lng, speed, heading) => {
    try { await api.post(`/trips/${id}/locations`, { lat, lng, speed, heading }); toast.success("Location shared"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitInspection = async () => {
    setBusy(true);
    try {
      await api.post("/driver/inspections", { vehicle_id: vehicleId, notes, status: "PASSED", checklist: { tires: true, brakes: true, lights: true, wheelchair_lift: true } });
      toast.success("Inspection submitted"); setInspectOpen(false); setNotes("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const submitIssue = async () => {
    setBusy(true);
    try {
      await api.post("/driver/incidents", { vehicle_id: vehicleId, ...issue });
      toast.success("Issue reported"); setIssueOpen(false); setIssue({ type: "MECHANICAL", severity: "LOW", description: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-md mx-auto" data-testid="driver-workspace">
      <PageHeader title="Driver Workspace" subtitle="Your trips, vehicle and daily checks." />
      <WorkSessionCard vehicleId={vehicleId} activeTripId={data.trips.find((t) => t.status === "IN_PROGRESS")?.id} />

      {/* Assigned vehicle */}
      <div className="border border-border rounded-lg bg-card p-5 mb-5">
        <div className="flex items-center gap-2 mb-3"><Truck className="w-4 h-4 text-brand" /><span className="text-xs uppercase tracking-wider text-muted-foreground">Assigned Vehicle</span></div>
        {data.vehicle ? (
          <div>
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg font-semibold">{data.vehicle.code} · {data.vehicle.name}</span>
              <StatusBadge value={data.vehicle.status} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">{data.vehicle.make} {data.vehicle.model} {data.vehicle.year} · Plate {data.vehicle.license_plate}</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button variant="outline" className="h-12" onClick={() => setInspectOpen(true)} data-testid="inspection-btn"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Inspection</Button>
              <Button variant="outline" className="h-12 text-danger border-danger/30" onClick={() => setIssueOpen(true)} data-testid="report-issue-btn"><AlertTriangle className="w-4 h-4 mr-1.5" /> Report Issue</Button>
            </div>
          </div>
        ) : <p className="text-sm text-muted-foreground">No vehicle assigned yet.</p>}
      </div>

      {/* Trips */}
      <div className="flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-brand" /><h2 className="font-heading font-semibold">Today's Trips</h2></div>
      <div className="space-y-3">
        {data.trips.length === 0 && <div className="border border-border rounded-lg bg-card p-6 text-center text-muted-foreground text-sm">No trips scheduled.</div>}
        {data.trips.map((t) => (
          <div key={t.id} className="border border-border rounded-lg bg-card p-4" data-testid="driver-trip-card">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold">{t.title}</span>
              <StatusBadge value={t.status} />
            </div>
            <p className="text-sm text-muted-foreground">{t.client_name}</p>
            <p className="text-xs text-muted-foreground mt-1">{t.scheduled_at ? `${t.scheduled_at.slice(0, 10)} ${t.scheduled_at.slice(11, 16)} · ` : ""}{t.pickup} → {t.dropoff}</p>
            {(t.passengers > 1 || t.wheelchair_required) && <p className="text-xs mt-1">{t.passengers || 1} passenger(s){t.wheelchair_required ? " · wheelchair" : ""}</p>}
            <div className="flex gap-2 mt-3">
              {t.status !== "IN_PROGRESS" ? (
                <Button className="flex-1 h-12" onClick={() => startTrip(t.id)} disabled={busy} data-testid="start-trip-btn"><Play className="w-4 h-4 mr-1.5" /> Start Trip</Button>
              ) : (
                <>
                  <Button className="flex-1 h-12" variant="outline" onClick={() => shareLocation(t.id)} data-testid="share-location-btn"><Navigation className="w-4 h-4 mr-1.5" /> Share Location</Button>
                  <Button className="flex-1 h-12 bg-danger hover:bg-danger/90" onClick={() => endTrip(t.id)} disabled={busy} data-testid="end-trip-btn"><Square className="w-4 h-4 mr-1.5" /> End</Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={inspectOpen} onOpenChange={setInspectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vehicle Inspection</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Tires, brakes, lights and wheelchair lift verified.</p>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="inspection-notes" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setInspectOpen(false)}>Cancel</Button><Button onClick={submitInspection} disabled={busy} data-testid="submit-inspection">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report an Issue</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={issue.description} onChange={(e) => setIssue({ ...issue, description: e.target.value })} data-testid="issue-desc" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setIssueOpen(false)}>Cancel</Button><Button className="bg-danger hover:bg-danger/90" onClick={submitIssue} disabled={busy} data-testid="submit-issue">Report</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
